import { EV } from '../core/eventbus.js';
import { Entity } from '../core/entity.js';
import { createVisitor } from '../entities/visitor.js';
import { drawVehicle } from '../render/vehicle_art.js';
import { VEHICLE_SIZE, STAGE_COUNT } from '../data/vehicles.js';
import { Repo } from '../data/schema.js';
import { MINUTES_PER_SECOND } from './time.js';

/**
 * 联机系统：把「我」的状态发出去，把「别人」变成场景里的访客。
 * 只依赖 NetAdapter 接口，换传输实现不用改这里。
 *
 * 认人不认名：身份一律用 uid（服务器账号发的号，没登录则是本机号）。
 *   跨设备 —— 一个 uid 就是一个人，同一账号开几个窗口也只算一个；
 *   本机多开 —— 附加上窗口号 tab，两个窗口两个角色，方便一个人自测。
 * 屋里时辰由 uid 最小的那位带着走，其他人跟着对齐，跨设备才不会各过各的日子。
 */

const SEND_INTERVAL = 0.1;   // 10Hz 广播
const PEER_TIMEOUT = 6;      // 秒，超时未收到状态就当人走了
const SMOOTH = 12;           // 插值追赶速度
const WORLD_INTERVAL = 2;    // 秒，时辰权威的广播间隔
const WORLD_GAP = 2;         // 游戏分钟：偏差超过这么多才校正，免得来回抖
const DAY = 1440;

export const PEER_COLORS = ['#6d8fa8', '#8fae6b', '#d9b45b', '#c98f8f', '#9a86b8', '#7fa8a0'];

export function installCoop(engine, net) {
  const { events } = engine;
  const peers = new Map();   // 身份 key -> 访客实体
  const cars = new Map();    // 身份 key#车号 -> 别人的车（影子车，只画不挡路）
  const acked = new Set();
  let acc = 0;
  let worldAcc = 0;
  let lastScene = '';
  let lastWorldAt = -1;      // 上次收到屋里时辰的时刻；-1 = 还没对齐过
  let lastAlignToast = -99;

  const me = engine.me || (engine.me = { name: '居者', color: PEER_COLORS[0] });

  /** 自己的账号 uid */
  const myUid = () => net.uid || net.selfId || '';
  /** 别人的账号 uid（老客户端只带 from 时退回它） */
  const uidOf = (msg) => msg.uid || msg.from || '';
  /** 一个身份一把钥匙：本机多开按窗口分，跨设备按账号分 */
  const keyOf = (msg) => (net.kind === 'local' ? `${uidOf(msg)}#${msg.tab || ''}` : uidOf(msg));
  const myKey = () => (net.kind === 'local' ? `${myUid()}#${net.tab}` : myUid());
  /** 屋里的时辰权威：在线 uid 里最小的那个，谁都算得出同一个结果 */
  function leaderUid() {
    let min = myUid();
    for (const e of peers.values()) {
      const u = e.visitor.uid || e.visitor.id || '';
      if (u && u < min) min = u;
    }
    return min;
  }

  /** 我现在在哪张图 */
  const sceneId = () => (engine.scene && engine.scene.id) || '';

  /** 这条消息是不是来自同一张图（老客户端不带 scene 的，照旧当同图） */
  function sameScene(msg) {
    const here = sceneId();
    if (!here) return true;
    const there = msg && msg.scene;
    if (!there) return true;
    return there === here;
  }

  /**
   * 本图里我的车：车只存在各自的存档里，不跟着状态发出去，别人就只看见你走、看不见你的车。
   * 正开着的那辆跟着人走，位置取人的坐标。
   */
  function myCars(p) {
    const db = engine.db;
    const tm = engine.scene && engine.scene.tilemap;
    if (!db || !tm) return [];
    const ts = tm.tileSize;
    const here = sceneId();
    return db.table('vehicles').all()
      .filter((r) => r.scene === here && Number(r.stage) >= STAGE_COUNT)
      .map((r) => {
        const riding = !!(p.veh && p.veh.rowId === r.id);
        return {
          id: r.id,
          kind: r.kind,
          broken: !!r.broken,
          x: Math.round(riding ? p.transform.x : Number(r.gx) * ts + ts / 2),
          y: Math.round(riding ? p.transform.y : Number(r.gy) * ts + ts / 2),
          angle: riding ? p.veh.angle : Number(r.angle) || 0,
        };
      });
  }

  function selfState() {
    const p = engine.scene && engine.scene.first('transform', 'player');
    if (!p) return null;
    return {
      x: p.transform.x, y: p.transform.y, facing: p.anim.facing, moving: p.anim.moving,
      scene: sceneId(),
      ride: p.veh ? p.veh.rowId : null,
      cars: myCars(p),
    };
  }

  /* ------------------------------ 别人的车 ------------------------------ */

  const carKeyOf = (owner, id) => `${owner}#${id}`;

  /** 按对方报上来的车单，添一辆 / 挪一辆 / 收走一辆 */
  function syncCars(owner, list) {
    if (!engine.scene) return;
    const seen = new Set();
    for (const c of list || []) {
      if (!c || !c.id) continue;
      const key = carKeyOf(owner, c.id);
      seen.add(key);
      let g = cars.get(key);
      if (!g || g.dead) {
        g = new Entity({
          name: c.kind || '车',
          transform: { x: c.x || 0, y: c.y || 0, w: VEHICLE_SIZE.w, h: VEHICLE_SIZE.h },
          car: { kind: c.kind || 'wreck', angle: c.angle || 0, broken: !!c.broken, tx: c.x || 0, ty: c.y || 0 },
        });
        g.drawable = {
          draw: (ctx, self, view) => drawVehicle(ctx, self.transform.x, self.transform.y, {
            kind: self.car.kind, angle: self.car.angle, broken: self.car.broken, time: view.time,
          }),
        };
        engine.scene.add(g);
        cars.set(key, g);
      } else {
        g.car.kind = c.kind || g.car.kind;
        g.car.angle = c.angle || 0;
        g.car.broken = !!c.broken;
      }
      g.car.tx = c.x || 0;
      g.car.ty = c.y || 0;
    }
    for (const [key, g] of [...cars]) {
      if (!key.startsWith(`${owner}#`) || seen.has(key)) continue;
      engine.scene.remove(g);
      cars.delete(key);
    }
  }

  /** 人走了（或换图了），他的车一并收走 */
  function clearCars(owner) {
    for (const [key, g] of [...cars]) {
      if (!key.startsWith(`${owner}#`)) continue;
      if (engine.scene) engine.scene.remove(g);
      cars.delete(key);
    }
  }

  function ensurePeer(msg) {
    const key = keyOf(msg);
    let e = peers.get(key);
    if (e) return e;
    e = createVisitor({
      id: key,
      name: String(msg.name || '客').slice(0, 6),
      color: msg.color || PEER_COLORS[peers.size % PEER_COLORS.length],
      x: msg.x || 0, y: msg.y || 0,
      facing: msg.facing || 'down',
    });
    e.visitor.uid = uidOf(msg);
    e.visitor.verified = !!msg.verified;
    e.visitor.scene = msg.scene || '';
    e.visitor.lastSeen = engine.elapsed;
    peers.set(key, e);
    if (engine.scene) engine.scene.add(e);
    events.emit(EV.NET_PEERS, peerList());
    return e;
  }

  function removePeer(id) {
    const e = peers.get(id);
    clearCars(id);
    if (!e) return;
    if (engine.scene) engine.scene.remove(e);
    peers.delete(id);
    acked.delete(id);
    events.emit(EV.NET_PEERS, peerList());
  }

  function removeAll() {
    for (const id of [...peers.keys()]) removePeer(id);
  }

  function peerList() {
    return [...peers.values()].map((e) => ({
      id: e.visitor.id,
      uid: e.visitor.uid,
      name: e.name,
      color: e.visitor.color,
      verified: !!e.visitor.verified,
    }));
  }

  /** 把本地时辰拉到屋里的时辰（只听权威的，偏差不大就不动） */
  function applyWorld(msg) {
    const db = engine.db;
    if (!db) return;
    const w = Repo.getWorld(db);
    // 消息在路上跑的那点时间补上（上限 15 秒，免得时钟差得离谱时乱跳）
    const lag = Math.max(0, Math.min(15, (net.now() - (msg.ts || 0)) / 1000)) * MINUTES_PER_SECOND;
    let m = (Number(msg.minute) || 0) + lag;
    let d = Number(msg.day) || w.day;
    while (m >= DAY) { m -= DAY; d += 1; }

    const target = d * DAY + m;
    const cur = (w.day || 1) * DAY + (w.minute || 0);
    const patch = {};
    if (msg.season && msg.season !== w.season) patch.season = msg.season;
    if (msg.weather && msg.weather !== w.weather) patch.weather = msg.weather;

    if (Math.abs(target - cur) > WORLD_GAP) {
      patch.day = d;
      patch.minute = m;
      Repo.saveWorld(db, patch);
      db._touch();
      if (engine.elapsed - lastAlignToast > 20) {
        lastAlignToast = engine.elapsed;
        events.emit(EV.TOAST, { text: '时辰已与屋里对齐', kind: 'info' });
      }
    } else if (Object.keys(patch).length) {
      Repo.saveWorld(db, patch);
    }
  }

  net.on((msg) => {
    if (!msg || keyOf(msg) === myKey()) return;
    if (typeof net.answerPing === 'function') net.answerPing(msg);

    // 不在一个屋里就别画进来：各走各的图，谁也看不见谁
    if (msg.t === 'state' || msg.t === 'hello' || msg.t === 'helloAck') {
      if (!sameScene(msg)) { removePeer(keyOf(msg)); return; }
    }

    switch (msg.t) {
      case 'hello': {
        ensurePeer(msg);
        if (!acked.has(keyOf(msg))) {
          acked.add(keyOf(msg));
          const s = selfState();
          if (s) net.send('helloAck', { name: me.name, color: me.color, verified: net.verified, ...s });
          // 新人进屋：把屋里现在的时辰告诉他，免得他先按自己的日子过一会儿
          if (leaderUid() === myUid()) sendWorld();
        }
        break;
      }
      case 'helloAck':
        ensurePeer(msg);
        break;
      case 'state': {
        const e = ensurePeer(msg);
        if (!e) break;
        // 对方改了称呼/颜色，就地更新并刷新 UI
        const nm = String(msg.name || e.name).slice(0, 6);
        if (nm !== e.name || (msg.color && msg.color !== e.visitor.color)) {
          e.name = nm;
          if (msg.color) e.visitor.color = msg.color;
          events.emit(EV.NET_PEERS, peerList());
        }
        if (msg.verified !== undefined) e.visitor.verified = !!msg.verified;
        if (msg.scene) e.visitor.scene = msg.scene;
        e.visitor.riding = !!msg.ride;      // 坐在车里就别再画两条腿了
        syncCars(keyOf(msg), msg.cars);
        e.visitor.tx = msg.x; e.visitor.ty = msg.y;
        e.visitor.facing = msg.facing || e.visitor.facing;
        e.visitor.moving = !!msg.moving;
        e.visitor.lastSeen = engine.elapsed;
        break;
      }
      case 'world': {
        // 只认比自己「更老」的那位；还没对齐过（刚进屋）时谁说的都先听着
        const from = uidOf(msg);
        if (from && from < myUid()) applyWorld(msg);
        else if (lastWorldAt < 0) applyWorld(msg);
        lastWorldAt = engine.elapsed;
        break;
      }
      case 'say': {
        const e = peers.get(keyOf(msg));
        if (e) {
          e.visitor.bubble = String(msg.text || '').slice(0, 40);
          e.visitor.bubbleUntil = engine.elapsed + 4;
        }
        break;
      }
      case 'voice': {
        // 语音不存不留，只在头顶挂个「正在说话」的小泡，响多久按这条的时长算
        const e = peers.get(keyOf(msg));
        if (e) {
          e.visitor.bubble = '（说话）';
          e.visitor.bubbleUntil = engine.elapsed + Math.max(2, Math.min(10, (msg.ms || 1000) / 1000));
        }
        break;
      }
      case 'bye':
        removePeer(keyOf(msg));
        break;
      default:
        break;
    }
  });

  /** 广播屋里的时辰（只有权威会调） */
  function sendWorld() {
    const db = engine.db;
    if (!db) return;
    const w = Repo.getWorld(db);
    net.send('world', {
      day: w.day, minute: w.minute, season: w.season, weather: w.weather, ts: net.now(),
    });
  }

  const system = {
    name: 'coop',
    update(dt) {
      const s = selfState();
      if (!s || !net.open) return;

      // 换了图，上一张图里别人的车全留不住
      if (lastScene !== s.scene) {
        lastScene = s.scene;
        for (const [key, g] of [...cars]) {
          if (engine.scene) engine.scene.remove(g);
          cars.delete(key);
        }
      }

      acc += dt;
      if (acc >= SEND_INTERVAL) {
        acc = 0;
        net.send('state', { name: me.name, color: me.color, verified: net.verified, ...s });
      }

      // 屋里时辰：只有权威发，其他人听着；权威走了，peer 超时后自然换人接棒
      if (leaderUid() === myUid()) {
        worldAcc += dt;
        if (worldAcc >= WORLD_INTERVAL) { worldAcc = 0; sendWorld(); }
      } else {
        worldAcc = 0;
      }

      for (const [id, e] of [...peers]) {
        const v = e.visitor;
        if (engine.elapsed - v.lastSeen > PEER_TIMEOUT) { removePeer(id); continue; }
        const dx = v.tx - e.transform.x;
        const dy = v.ty - e.transform.y;
        const k = Math.min(1, dt * SMOOTH);
        e.transform.x += dx * k;
        e.transform.y += dy * k;
        const dist = Math.hypot(dx, dy);
        e.anim.facing = v.facing;
        e.anim.moving = dist > 3;
        if (e.anim.moving) e.anim.phase += dt * 10;
      }

      // 别人的车同样插值追过去，免得一跳一跳
      for (const g of cars.values()) {
        if (!g || g.dead || !g.car) continue;
        const k = Math.min(1, dt * SMOOTH);
        g.transform.x += (g.car.tx - g.transform.x) * k;
        g.transform.y += (g.car.ty - g.transform.y) * k;
      }
    },
  };

  // 切换场景：不在同一张图的客人请出去，其余重新挂到新场景
  events.on(EV.SCENE_ENTER, () => {
    if (!engine.scene) return;
    engine.scene.use(system);
    for (const [id, e] of [...peers]) {
      if (e.visitor.scene && e.visitor.scene !== sceneId()) { removePeer(id); continue; }
      if (!engine.scene.entities.has(e.id)) engine.scene.add(e);
    }
    // 换图后立刻吆喝一声（带新的图上名），同图的人马上能看见我
    acc = SEND_INTERVAL;
  });
  if (engine.scene) engine.scene.use(system);

  // 关页面 / 切走时主动告辞，别让别人等你六秒才发现你走了
  if (typeof window !== 'undefined') {
    const bye = () => { if (net.open) net.send('bye', {}); };
    window.addEventListener('pagehide', bye);
    window.addEventListener('beforeunload', bye);
  }

  return {
    system,
    peers,
    peerList,
    /** 屋里连我在内一共几个人（按账号算） */
    count() { return peers.size + 1; },
    /** 当前时辰权威的 uid，方便面板提示「你在带着屋里走时辰」 */
    leader() { return leaderUid(); },
    isLeader() { return leaderUid() === myUid(); },
    say(text) {
      if (!net.open || !text) return;
      const t = String(text).slice(0, 40);
      net.send('say', { text: t });
      // 本地也给个气泡反馈
      const p = engine.scene && engine.scene.first('transform', 'player');
      if (p) events.emit(EV.TOAST, { text: '你说：' + t, kind: 'info' });
    },
    leave() {
      if (net.open) net.send('bye', {});
      removeAll();
      lastWorldAt = -1;   // 下次进屋重新跟着屋里的时辰走
      worldAcc = 0;
    },
  };
}
